/**
 * src/player/engine.ts — O MOTOR. Lógica pura, ZERO DOM, ZERO rede.
 *
 * É aqui que mora o PLAYER §4.2/§4.3 e é aqui que a suíte de testes manda. Nada
 * neste arquivo conhece `<video>`, `fetch`, `localStorage` ou `window`: ele recebe
 * uma fila, um relógio e callbacks, e diz o que tocar e quando. Quem executa no
 * browser é `src/ui/player.ts`; quem manda o próximo é o próprio motor.
 *
 * AS CINCO REGRAS QUE ESTE ARQUIVO IMPLEMENTA (cada uma com o parágrafo que a
 * originou, porque cada uma já custou bug no produto):
 *
 *  1. ORDEM E DURAÇÃO NÃO SÃO DECIDIDAS AQUI. `items` chega na ordem do loop,
 *     pronta (`buildPlaybackQueue` no backend). O motor não ordena, não embaralha,
 *     não decide duração — §4.2 "qualquer lógica de rotação duplicada no receptor é
 *     uma segunda implementação para divergir".
 *  2. IMAGEM = `durationSeconds`; VÍDEO = até o fim, sem esperar o `durationSeconds`
 *     por cima. Fim de vídeo ⇒ próximo item imediatamente, sem pausa preta (§4.2).
 *  3. FIM DO LOOP VOLTA AO PRIMEIRO, sem pausa e sem tela preta (§4.2).
 *  4. ITEM QUE NÃO EXIBE É PULADO, nunca trava o loop esperando mídia (§4.5.3).
 *  5. FILA NOVA NÃO CORTA O ITEM ATUAL: a nova fila espera o item atual terminar
 *     (§4.3). Candidatos são revalidados antes de cada exibição pelo relógio
 *     injetado; a casca controla a retirada imediata por vencimento (§4.4).
 *
 * RELÓGIO INJETADO: o motor nunca chama `Date.now()`. Todo tempo vem de `now()`.
 * É o que torna "anúncio até 22:00 sai às 22:00 offline" testável sem esperar 22:00
 * (critério 7 da §9) — e é o mesmo mecanismo que o dev-pleno precisa para o critério 6.
 */
import type { DisplayItem } from './queue';
import { ActiveQueue } from './queue';

/**
 * `fit` enviado para `EngineHost.show` — é o que a superfície aplica como
 * `object-fit` no `<img>`/`<video>`. Deriva do `scaleMode` do anúncio: a
 * escolha do gestor precisa ser honrada pela TV (PLAYER §4.5). `cover` corta;
 * `stretch` distorce; `contain` (o default legado) mostra inteiro.
 */
export type DisplayFit = 'contain' | 'cover' | 'stretch';

export function displayFit(item: Pick<DisplayItem, 'scaleMode'>): DisplayFit {
  // A ausência de `scaleMode` no item é o sinal de "não sei" — o player usa
  // `contain`, que é o que ele já fazia antes deste campo existir (e é o que
  // a maioria dos anúncios manda quando não se importam com o modo).
  return item.scaleMode ?? 'contain';
}

/** Relógio injetável. `nowMs()` em epoch ms; o motor converte para segundos. */
export interface EngineClock {
  nowMs(): number;
}

/** Relógio de verdade (o único lugar do receptor que toca em `Date.now`). */
export const systemEngineClock: EngineClock = {
  nowMs: () => Date.now(),
};

/** Relógio de teste: avança por conta própria, sem esperar nada. */
export function manualEngineClock(startMs = 0): EngineClock & { advanceMs(ms: number): void; setMs(ms: number): void } {
  let current = startMs;
  return {
    nowMs: () => current,
    advanceMs: (ms: number) => {
      current += ms;
    },
    setMs: (ms: number) => {
      current = ms;
    },
  };
}

/** Por que um item saiu do ar. Alimenta log/diagnóstico — NUNCA a tela (§8). */
export type ItemEndReason =
  | 'timer' /** imagem: passou `holdSeconds` */
  | 'media-end' /** vídeo: chegou no fim */
  | 'skipped-unavailable' /** não tinha mídia disponível (PLAYER §4.5.3) */
  | 'skipped-failed' /** mídia foi baixada mas não decodificou (PLAYER §6) */
  | 'empty-queue'; /** a fila ficou vazia: vai para a tela de espera, nunca preta */

/** O que o motor pede ao mundo para exibir um item. */
export type EngineCommand =
  | {
      kind: 'show';
      item: DisplayItem;
      /** Modo de escala (PLAYER §4.5) — derivado do `scaleMode` do anúncio. */
      fit: DisplayFit;
    }
  | { kind: 'clear'; reason: 'new-queue-empty' };

/** Callbacks: o motor fala, o mundo obedece. */
export interface EngineHost {
  /** Prepara e exibe o item. Resolve quando a mídia ESTÁ no ar (ou rejeita = falhou). */
  show(command: Extract<EngineCommand, { kind: 'show' }>): Promise<void>;
  /** Tira a mídia da tela sem mostrar nada (nunca deixa preto por acidente). */
  clear(reason: 'new-queue-empty'): Promise<void>;
  /** Diagnóstico: item entrou/saiu, item pulado, fila aplicada. Vai para log, não para a TV. */
  onLog?(event: string, detail?: Record<string, unknown>): void;
  /** Chamado a cada troca de item — a casca usa para o `currentAdId` do heartbeat. */
  onItemChanged?(item: DisplayItem | null): void;
}

export interface EngineOptions {
  clock: EngineClock;
  host: EngineHost;
  /**
   * Teto de segurança por item (ms). Um vídeo que nunca emite `ended` (codec
   * esquisito, arquivo truncado) TRAVARIA a TV para sempre num único anúncio — que é
   * pior que pular. O teto usa a duração declarada + 30 s de folga, com piso de 20 s:
   * o motor nunca espera indefinidamente por uma mídia (PLAYER §6, "contrato implícito").
   */
  videoTimeoutSlackMs?: number;
  videoTimeoutFloorMs?: number;
}

const DEFAULT_VIDEO_SLACK_MS = 30_000;
const DEFAULT_VIDEO_FLOOR_MS = 20_000;

/**
 * O MOTOR. Uma instância por execução do receptor.
 *
 * Uso: `engine.setQueue(new ActiveQueue(payload, now))` e o motor cuida do resto.
 * A troca de fila NUNCA é imediata se há item no ar (regra 5).
 */
export class PlayerEngine {
  private queue: ActiveQueue | undefined;
  /** Índice do item no ar. `-1` = nada exibindo. */
  private index = -1;
  private running = false;
  private readonly clock: EngineClock;
  private readonly host: EngineHost;
  private readonly videoSlackMs: number;
  private readonly videoFloorMs: number;

  /** Instante (ms do relógio) em que o item atual começou. */
  private itemStartedAtMs = 0;
  /** Fila que chegou enquanto o item atual estava no ar, à espera de aplicação (§4.3). */
  private pending: ActiveQueue | null | undefined;
  private generation = 0;

  constructor(options: EngineOptions) {
    this.clock = options.clock;
    this.host = options.host;
    this.videoSlackMs = options.videoTimeoutSlackMs ?? DEFAULT_VIDEO_SLACK_MS;
    this.videoFloorMs = options.videoTimeoutFloorMs ?? DEFAULT_VIDEO_FLOOR_MS;
  }

  /** `version` da fila em execução (para o heartbeat e para o `If-None-Match`). */
  get currentVersion(): string | undefined {
    return this.queue?.version;
  }

  /** Item no ar agora, ou `null`. */
  get currentItem(): DisplayItem | null {
    const item = this.current();
    return item ?? null;
  }

  /** Loop em execução (isto é, há um item no ar). */
  get isPlaying(): boolean {
    return this.running;
  }

  /**
   * RECEBE UMA FILA. Regra 5 (§4.3): se há item no ar, a fila ESPERA; se não há
   * (primeira fila, ou loop vazio), entra já.
   *
   * `null` = nenhuma fila utilizável (cache inválido, 304 sem nunca ter baixo) — o
   * motor fica na tela de espera, nunca em preto.
   *
   * Devolve uma Promise justamente para que quem chama possa saber quando o PRIMEIRO
   * item já está no ar (os testes dependem disso, e a UI também: é o que permite
   * trocar a tela de "Conectado! Buscando anúncios..." pela mídia sem piscar).
   */
  async setQueue(queue: ActiveQueue | null): Promise<void> {
    if (this.running) {
      // Não corta o que o cliente está vendo. Guarda e aplica no fim do item.
      this.pending = queue;
      this.host.onLog?.('fila-adiada', { version: queue?.version, size: queue?.size ?? 0 });
      return;
    }
    await this.applyQueue(queue ?? undefined);
  }

  /** Aplica imediatamente, sem esperar (usado quando não há nada no ar). */
  private async applyQueue(queue: ActiveQueue | undefined): Promise<void> {
    this.generation += 1;
    this.pending = undefined;
    this.queue = queue;
    this.index = -1;
    if (!queue || queue.isEmpty) {
      await this.finishLoop('empty-queue');
      return;
    }
    await this.startNext();
  }

  private current(): DisplayItem | undefined {
    const items = this.queue?.items();
    if (!items || this.index < 0 || this.index >= items.length) {
      return undefined;
    }
    return items[this.index];
  }

  /** Avança o índice; no fim do loop volta ao PRIMEIRO (regra 3), sem pausa. */
  private advance(): void {
    const items = this.queue?.items() ?? [];
    if (items.length === 0) {
      this.index = -1;
      return;
    }
    this.index = this.index + 1 >= items.length ? 0 : this.index + 1;
  }

  /** Coloca no ar o item do índice atual. Falha de mídia ⇒ PULA (regra 4). */
  private async startNext(): Promise<void> {
    if (!this.queue || this.queue.isEmpty) {
      await this.finishLoop('empty-queue');
      return;
    }
    if (this.index < 0) {
      this.index = 0;
    }

    const items = this.queue.items();
    // Teto de tentativas por volta: se TODOS os itens falharem, para de tentar em
    // loop feveroso (cada tentativa baixa mídia) e vai para a tela de espera.
    let failures = 0;
    const maxFailures = Math.max(1, items.length);

    while (this.queue && failures < maxFailures) {
      const item = this.current();
      if (!item) {
        await this.finishLoop('empty-queue');
        return;
      }

      if (!this.queue.isActive(item, new Date(this.clock.nowMs()))) {
        failures += 1;
        this.advance();
        continue;
      }

      const attempt = ++this.generation;
      try {
        await this.host.show({ kind: 'show', item, fit: displayFit(item) });
        if (attempt !== this.generation) {
          return; // uma fila nova chegou enquanto carregava: o show foi invalidado.
        }
        this.running = true;
        this.itemStartedAtMs = this.clock.nowMs();
        this.host.onItemChanged?.(item);
        this.host.onLog?.('item no ar', {
          adId: item.adId,
          kind: item.kind,
          holdSeconds: item.holdSeconds,
          advancesOn: item.advancesOn,
        });
        return;
      } catch (error) {
        if (attempt !== this.generation) {
          return;
        }
        failures += 1;
        this.host.onLog?.('item pulado (mídia indisponível)', {
          adId: item.adId,
          error: String(error),
          failures,
        });
        this.host.onItemChanged?.(null);
        this.advance();
      }
    }

    // Todos os itens desta volta falharam: o lojista precisa ver ALGUMA coisa (§6).
    await this.finishLoop('skipped-failed');
  }

  /**
   * O item atual terminou. Decide o que vem agora:
   *  - se entrou uma fila nova enquanto o item estava no ar ⇒ ela vence (§4.3);
   *  - senão, próximo item do loop.
   */
  private async onItemFinished(reason: ItemEndReason): Promise<void> {
    this.running = false;
    this.host.onLog?.('item terminou', { reason, adId: this.current()?.adId });

    if (this.pending !== undefined) {
      const next = this.pending;
      this.pending = undefined;
      this.host.onLog?.('fila aplicada (§4.3, sem cortar o item atual)', {
        version: next?.version,
        size: next?.size ?? 0,
      });
      await this.applyQueue(next ?? undefined);
      return;
    }

    if (reason === 'empty-queue') {
      await this.finishLoop('empty-queue');
      return;
    }

    this.advance();
    await this.startNext();
  }

  /** Loop sem nada exibível: limpa a tela e fica esperando (nunca preta, §4.6). */
  private async finishLoop(reason: ItemEndReason): Promise<void> {
    this.running = false;
    this.index = -1;
    this.host.onItemChanged?.(null);
    this.host.onLog?.('loop sem itens', { reason });
    try {
      await this.host.clear('new-queue-empty');
    } catch {
      // Uma falha ao limpar é do DOM, não da fila: o motor não pode morrer por isso.
      this.host.onLog?.('falha ao limpar tela', { reason });
    }
  }

  /**
   * QUANDO TROCAR DE ITEM — chamado pelo `ui/player.ts` no `setTimeout` da imagem ou
   * no `ended` do vídeo. Ponto único de avanço: os dois caminhos convergem aqui, e é
   * por isso que vídeo nunca "volta a esperar" depois de acabar (§4.2).
   */
  async notifyItemEnded(reason: ItemEndReason = 'timer'): Promise<void> {
    if (!this.running) {
      return;
    }
    await this.onItemFinished(reason);
  }

  /**
   * Quanto falta, em ms, para o motor ter AVANÇADO SOZINHO — usado só para o teto de
   * segurança do vídeo. Para imagem o `ui/player.ts` já agenda pelo próprio
   * `holdSeconds` (é o relógio que manda, e o relógio é quem atrasa).
   */
  remainingMs(): number {
    const item = this.current();
    if (!item || item.advancesOn !== 'media-end') {
      return Number.POSITIVE_INFINITY;
    }
    const declared = item.media?.durationSeconds ?? item.holdSeconds;
    const budgetMs = Math.max(this.videoFloorMs, (declared + this.videoSlackMs / 1000) * 1000);
    return budgetMs - (this.clock.nowMs() - this.itemStartedAtMs);
  }

  /** O motor já cumpriu o tempo do vídeo (teto): o `ui` chama `notifyItemEnded`. */
  videoWatchdogExpired(): boolean {
    return this.remainingMs() <= 0;
  }

  /** Called by the shell's corrected-clock schedule tick; expiry may cut an item. */
  async revalidateCurrent(): Promise<void> {
    const item = this.current();
    if (this.running && item && this.queue &&
      !this.queue.isActive(item, new Date(this.clock.nowMs()))) {
      await this.onItemFinished('timer');
    }
  }

  /** Para o motor. Usado no teardown e nos testes. */
  async stop(): Promise<void> {
    this.generation += 1;
    const had = this.running;
    this.running = false;
    this.pending = undefined;
    this.index = -1;
    if (had) {
      this.host.onItemChanged?.(null);
    }
  }
}
